/**
 * The interface level of the three-level provider editor (ADR-040 §D-1; Flow V
 * Frame 3).
 *
 * Sits below the provider form in the same scrollable pane. Each interface is a
 * row that **expands in place** into its own editor — which itself contains the
 * connection level — so the containment the data model has (a connection belongs
 * to an interface belongs to a provider) is the containment the surface shows.
 *
 * # No sub-tabs (ADR-024 §4)
 *
 * Rows expand rather than switching a pane. Sub-tabs would hide two of the three
 * levels at all times, and this dialog is opened as often to *stop* something as
 * to configure it — a kill switch two clicks behind a tab bar is one an operator
 * hesitates over.
 *
 * # One expanded at a time
 *
 * Not a fashion: two open interface editors are two independent unsaved drafts
 * of live ingest configuration, and the operator would have no way to tell which
 * Save belonged to which without reading the whole pane.
 *
 * # A projected provider says where it came from
 *
 * A provider created by migration 066 out of the retired aggregator registry
 * carries **no interfaces** — an aggregator row held identity and trust facts
 * only, never an endpoint or a type. Its empty state therefore states the reason
 * rather than reading as an ordinary empty provider, because without that line a
 * projected row is indistinguishable from a provider whose interfaces someone
 * deleted, and on ingress "nothing configured" and "somebody removed it" call
 * for very different next acts (Flow V Frame 2).
 *
 * Cross-references:
 *   - ADR-024 §4; ADR-040 §D-1; Flow V Frames 2–3, §5a, §5b.
 *   - REQ-UI-156 (the three levels), -159, -163.
 */
import { useMemo } from "react";
import {
  findProviderInterfaceType,
  isReservedProviderType,
  type InterfaceState,
} from "@/providers/provider-catalog";
import { resolveEffectiveState, type ChainLink } from "@/peers/peer-effective-state";
import { PeerSection } from "@/peers/peer-section";
import { StatePill } from "@/peers/peer-state-controls";
import { PeerSuppressionNote } from "@/peers/peer-suppression-note";
import { ProviderInterfaceEditor } from "@/providers/provider-interface-editor";
import { ProviderInterfaceSummary } from "@/providers/provider-interface-summary";
import {
  isPrivilegedProviderInterfaceView,
  toProviderInterfaceConfig,
  type ProviderInterfaceConfig,
  type ProviderInterfaceView,
} from "@/providers/provider-model";
import { cn } from "@/lib/utils";

/** Props for {@link ProviderInterfacesSection}. */
export interface ProviderInterfacesSectionProps {
  /** The owning provider's name and state — the ancestor chain's only link here. */
  provider: ChainLink;
  /**
   * Whether this provider was projected out of the retired aggregator registry,
   * so the empty state can say so instead of reading as an alarm.
   */
  projected?: boolean;
  interfaces: readonly ProviderInterfaceView[];
  isLoading: boolean;
  error: string | null;
  /** The expanded interface, or `null`. One at a time — see the module docstring. */
  expandedId: string | null;
  onExpand: (interfaceId: string | null) => void;
  mayMutate: boolean;
  /** Renders the create affordance and the draft row. */
  onCreate: () => void;
  /** The unsaved draft, when one is being created. */
  draft: ProviderInterfaceConfig | null;
  onSave: (config: ProviderInterfaceConfig) => void;
  onDelete: (interfaceId: string) => void;
  onCancelDraft: () => void;
  onSetInterfaceState: (interfaceId: string, state: InterfaceState) => void;
  onSetConnectionState: (interfaceId: string, connectionId: string, state: InterfaceState) => void;
  /** Delete one persisted connection on the server (REQ-UI-159). */
  onDeleteConnection: (interfaceId: string, connectionId: string) => void;
  savePending?: boolean;
  statePending?: boolean;
  saveError?: string | null;
  /** Mints ids for new connections; injectable so tests are deterministic. */
  makeId?: () => string;
}

/** Render the interfaces level and, expanded within it, the connection level. */
export function ProviderInterfacesSection({
  provider,
  projected = false,
  interfaces,
  isLoading,
  error,
  expandedId,
  onExpand,
  mayMutate,
  onCreate,
  draft,
  onSave,
  onDelete,
  onCancelDraft,
  onSetInterfaceState,
  onSetConnectionState,
  onDeleteConnection,
  savePending = false,
  statePending = false,
  saveError,
  makeId,
}: ProviderInterfacesSectionProps): React.ReactElement {
  const ancestors: readonly ChainLink[] = [provider];
  return (
    <PeerSection
      id="provider-interfaces"
      title="Interfaces"
      action={
        mayMutate && (
          <button
            type="button"
            onClick={onCreate}
            data-testid="provider-add-interface"
            className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
          >
            Add interface
          </button>
        )
      }
    >
      {isLoading ? (
        <p className="text-body-sm text-text-secondary">Loading interfaces…</p>
      ) : error !== null ? (
        <p role="alert" className="text-body-sm text-destructive">
          Could not load this provider&rsquo;s interfaces: {error}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {interfaces.length === 0 &&
            draft === null &&
            (projected ? (
              <p
                className="text-body-sm text-text-secondary"
                data-testid="provider-interfaces-projected"
              >
                This provider was projected from the retired aggregator registry and has no
                interfaces yet. Add one, or install a provider bundle.
              </p>
            ) : (
              <p className="text-body-sm text-text-muted" data-testid="provider-interfaces-empty">
                This provider has no interfaces, so nothing is arriving from it.
              </p>
            ))}
          {interfaces.map((view) => (
            <InterfaceRow
              key={view.interface_id}
              view={view}
              ancestors={ancestors}
              expanded={expandedId === view.interface_id}
              onToggle={() => onExpand(expandedId === view.interface_id ? null : view.interface_id)}
              mayMutate={mayMutate}
              onSave={onSave}
              onDelete={() => onDelete(view.interface_id)}
              onCancel={() => onExpand(null)}
              otherNames={interfaces
                .filter((other) => other.interface_id !== view.interface_id)
                .map((other) => other.name)}
              onSetState={(state) => onSetInterfaceState(view.interface_id, state)}
              onSetConnectionState={(connectionId, state) =>
                onSetConnectionState(view.interface_id, connectionId, state)
              }
              onDeleteConnection={(connectionId) =>
                onDeleteConnection(view.interface_id, connectionId)
              }
              savePending={savePending}
              statePending={statePending}
              saveError={saveError}
              makeId={makeId}
            />
          ))}
          {draft !== null && (
            <div
              data-testid="provider-interface-draft"
              className="rounded border border-brand bg-bg-base"
            >
              <div className="border-b border-border px-3 py-2 text-body-sm text-text-primary">
                New interface (unsaved)
              </div>
              <ProviderInterfaceEditor
                key={draft.interface_id}
                value={draft}
                isNew
                otherNames={interfaces.map((other) => other.name)}
                makeId={makeId}
                savePending={savePending}
                statePending={statePending}
                saveError={saveError}
                ancestors={ancestors}
                onSave={onSave}
                onDelete={onCancelDraft}
                onCancel={onCancelDraft}
                onSetState={() => undefined}
                onSetConnectionState={() => undefined}
              />
            </div>
          )}
        </div>
      )}
    </PeerSection>
  );
}

/** One interface: a summary row that expands into its editor or its read view. */
function InterfaceRow({
  view,
  ancestors,
  expanded,
  onToggle,
  mayMutate,
  onSave,
  onDelete,
  onCancel,
  otherNames,
  onSetState,
  onSetConnectionState,
  onDeleteConnection,
  savePending,
  statePending,
  saveError,
  makeId,
}: {
  view: ProviderInterfaceView;
  ancestors: readonly ChainLink[];
  expanded: boolean;
  onToggle: () => void;
  mayMutate: boolean;
  onSave: (config: ProviderInterfaceConfig) => void;
  onDelete: () => void;
  onCancel: () => void;
  otherNames: readonly string[];
  onSetState: (state: InterfaceState) => void;
  onSetConnectionState: (connectionId: string, state: InterfaceState) => void;
  onDeleteConnection: (connectionId: string) => void;
  savePending?: boolean;
  statePending?: boolean;
  saveError?: string | null;
  makeId?: () => string;
}): React.ReactElement {
  const descriptor = findProviderInterfaceType(view.type_id);
  const reserved = isReservedProviderType(view.type_id);
  const resolved = resolveEffectiveState(ancestors, view.state);
  // Editable only when the session may mutate, the response carried the fields
  // an editor is made of, and the type is one this build can actually realize.
  // Each of the three is a separate reason and none implies the others.
  const editable = mayMutate && isPrivilegedProviderInterfaceView(view) && !reserved;
  const bodyId = `provider-interface-body-${view.interface_id}`;

  /**
   * The editor's seed, memoized on the **row** rather than built inline.
   *
   * ⚠ Load-bearing, not a micro-optimisation. The editor re-seeds its draft when
   * this prop's identity changes, and this component re-renders whenever any
   * sibling-driven prop moves — `savePending`, `statePending`, `saveError`, or a
   * background refetch. Built inline, `toProviderInterfaceConfig(view)` would
   * mint a fresh object on each of those, so starting a connection while a name
   * was half-typed would silently discard the typing. Memoizing on `view` ties
   * the identity to the underlying row, which is what "the server sent something
   * new" actually means.
   */
  const editorSeed = useMemo(
    () => (editable ? toProviderInterfaceConfig(view) : null),
    [editable, view],
  );

  return (
    <section
      data-testid={`provider-interface-row-${view.name}`}
      className={cn("rounded border bg-bg-base", expanded ? "border-brand" : "border-border")}
    >
      <h4>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={bodyId}
          data-testid={`provider-interface-toggle-${view.name}`}
          className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left hover:bg-bg-elevated"
        >
          <span aria-hidden="true" className="text-meta text-text-secondary">
            {expanded ? "▾" : "▸"}
          </span>
          <span className="min-w-0 flex-1 truncate text-body-sm text-text-primary">
            {view.name}
          </span>
          {reserved && (
            <span
              data-testid={`provider-interface-reserved-${view.name}`}
              className="rounded border border-border px-1.5 py-0.5 text-meta text-text-secondary"
            >
              Reserved — not implemented
            </span>
          )}
          <StatePill state={view.state} />
          <PeerSuppressionNote resolved={resolved} testId={`provider-interface-${view.name}`} />
        </button>
      </h4>
      <p className="px-3 pb-2 text-meta text-text-secondary">
        {descriptor?.label ?? view.type_id} · {view.connections.length}{" "}
        {view.connections.length === 1 ? "connection" : "connections"}
        {view.connections.length > 0 &&
          ` · ${view.connections.filter((c) => c.state === "started").length} started`}
      </p>
      {expanded && (
        <div id={bodyId} className="border-t border-border">
          {reserved && (
            <p
              className="px-4 py-3 text-body-sm text-text-muted"
              data-testid={`provider-interface-reserved-note-${view.name}`}
            >
              This interface type is registered so it can be configured and recognized, but Meridian
              has no implementation for it. It cannot be started, and its configuration is shown
              read-only.
            </p>
          )}
          {editorSeed !== null ? (
            <ProviderInterfaceEditor
              value={editorSeed}
              isNew={false}
              otherNames={otherNames}
              makeId={makeId}
              savePending={savePending}
              statePending={statePending}
              saveError={saveError}
              ancestors={ancestors}
              onSave={onSave}
              onDelete={onDelete}
              onCancel={onCancel}
              onSetState={onSetState}
              onSetConnectionState={onSetConnectionState}
              onDeleteConnection={onDeleteConnection}
            />
          ) : (
            <ProviderInterfaceSummary view={view} ancestors={ancestors} />
          )}
        </div>
      )}
    </section>
  );
}
